import { createElement } from 'react';
import type { ReactElement } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { SxProps, Theme } from '@mui/material';
import { NOTIFICATION_VISUAL_THEME, type NotificationVisual } from './NotificationCenter.constants';
import {
  cardCompletedOlderSx,
  cardCompletedRecentSx,
  cardFailedReadSx,
  cardFailedSx,
  cardStartedSx,
  iconCircleCompletedSx,
  iconCircleFailedSx,
  iconCircleSx,
  notificationPanelCardSx,
} from './NotificationCenter.styles';
import { NOTIFICATION_VARIANT, type NotificationVariant } from './NotificationCenter.types';

export function notificationVisualForVariant(variant: NotificationVariant): NotificationVisual {
  if (variant === NOTIFICATION_VARIANT.COMPLETED) {
    return 'success';
  }
  if (variant === NOTIFICATION_VARIANT.FAILED) {
    return 'error';
  }
  return 'active';
}

const ICON_COMPONENTS = {
  success: CheckCircleOutlineIcon,
  error: ErrorOutlineIcon,
  active: AutoAwesomeIcon,
} as const;

const ICON_CIRCLE_SX: Record<NotificationVisual, SxProps<Theme>> = {
  success: iconCircleCompletedSx,
  error: iconCircleFailedSx,
  active: iconCircleSx,
};

const TOAST_OUTER_SX: Partial<Record<NotificationVariant, SxProps<Theme>>> = {
  [NOTIFICATION_VARIANT.FAILED]: cardFailedSx,
  [NOTIFICATION_VARIANT.COMPLETED]: cardCompletedRecentSx,
  [NOTIFICATION_VARIANT.STARTED]: cardStartedSx,
  [NOTIFICATION_VARIANT.GLOBAL]: cardCompletedOlderSx,
  [NOTIFICATION_VARIANT.GENERAL]: cardCompletedOlderSx,
};

export function getToastOuterSx(variant: NotificationVariant): SxProps<Theme> | undefined {
  return TOAST_OUTER_SX[variant];
}

/** Colored chrome for unread panel rows (not PROCESSING — that uses gradient wrapper in the card). */
const PANEL_CARD_UNREAD_SX: Record<NotificationVariant, (isRecent: boolean) => SxProps<Theme>> = {
  [NOTIFICATION_VARIANT.FAILED]: () => ({ ...cardFailedSx, background: '#fff', backgroundColor: '#fff' }),
  [NOTIFICATION_VARIANT.COMPLETED]: isRecent =>
    isRecent ? cardCompletedRecentSx : cardCompletedOlderSx,
  [NOTIFICATION_VARIANT.STARTED]: () => cardStartedSx,
  [NOTIFICATION_VARIANT.PROCESSING]: () => cardCompletedOlderSx,
  [NOTIFICATION_VARIANT.GLOBAL]: () => cardCompletedOlderSx,
  [NOTIFICATION_VARIANT.GENERAL]: () => cardCompletedOlderSx,
};

/**
 * Read: neutral white card + thin border + shadow.
 * Unread: original variant colors/borders (PROCESSING uses gradient frame in NotificationCard).
 */
export function getCardSurfaceSx(
  variant: NotificationVariant,
  isRecent: boolean,
  options?: { read?: boolean }
): SxProps<Theme> {
  if (options?.read) {
    if (variant === NOTIFICATION_VARIANT.FAILED) {
      return cardFailedReadSx;
    }
    return notificationPanelCardSx;
  }
  return PANEL_CARD_UNREAD_SX[variant](isRecent);
}

export function getNotificationIconCircleSx(variant: NotificationVariant): SxProps<Theme> {
  return ICON_CIRCLE_SX[notificationVisualForVariant(variant)];
}

export function getNotificationIcon(variant: NotificationVariant): ReactElement {
  const visual = notificationVisualForVariant(variant);
  const color = NOTIFICATION_VISUAL_THEME[visual].iconColor;
  const Component = ICON_COMPONENTS[visual];
  return createElement(Component, { sx: { fontSize: 18, color } });
}

export function getNotificationTitleColor(variant: NotificationVariant): string {
  const visual = notificationVisualForVariant(variant);
  return NOTIFICATION_VISUAL_THEME[visual].titleColor;
}
